/**
 * Brain updates that run in the background.
 *
 * Saving or deleting a Brain document used to hold the user on a modal until the
 * whole indexing pipeline (chunking, embeddings, graph) finished — up to a
 * minute. Now the page only waits for the save and hands the
 * indexing to this provider, which lives above the router so it keeps going
 * when the user moves to another screen, and tells them how it ended with a
 * notification.
 *
 * The work is still a request the browser holds open (the server needs that to
 * do it), so closing the tab would cut off the follow-up: the provider asks the
 * browser to confirm before leaving while anything is running.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { notification } from 'antd';
import { useActiveOrganization } from '../hooks/useActiveOrganization';
import { deindexBrainPaths, fetchBrainPathIndexState, indexBrainFile } from '../services/api.service';
import { runIndexingJob, type IndexingJobDeps, type IndexingJobSpec } from '../utils/brainIndexingJob';
import { describeOutcome, type NoticeLevel } from '../utils/brainIndexingMessages';

interface BrainIndexingContextValue {
  /** Starts the indexing of a change that is already saved, and notifies when it ends. */
  trackIndexing: (spec: IndexingJobSpec) => void;
  /** How many updates are still running. */
  running: number;
  /** How many have finished since the app loaded: a screen showing indexed data (the graph) refreshes when it changes. */
  finished: number;
}

const BrainIndexingContext = createContext<BrainIndexingContextValue>({
  trackIndexing: () => undefined,
  running: 0,
  finished: 0,
});

const REAL_DEPS: IndexingJobDeps = {
  run: (spec) =>
    spec.action === 'index' ? indexBrainFile(spec.paths[0] ?? '') : deindexBrainPaths(spec.paths),
  state: fetchBrainPathIndexState,
  sleep: (ms) => new Promise((resolve) => window.setTimeout(resolve, ms)),
  now: () => Date.now(),
};

// Seconds a notification stays up; 0 = until the user closes it. A failure must
// not vanish on its own: it is the one the user has to act on.
const NOTICE_SECONDS: Record<NoticeLevel, number> = { success: 7, warning: 12, error: 0 };

export const BrainIndexingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const organization = useActiveOrganization();
  const organizationRef = useRef(organization);
  organizationRef.current = organization;

  const [running, setRunning] = useState(0);
  const [finished, setFinished] = useState(0);

  const trackIndexing = useCallback((spec: IndexingJobSpec) => {
    // The name is fixed when the update starts: the result must name the
    // organization that was updated, even if the user switches meanwhile.
    const organizationName = organizationRef.current.name;
    setRunning((count) => count + 1);

    void runIndexingJob(spec, REAL_DEPS)
      .then((outcome) => {
        const notice = describeOutcome(spec, outcome, organizationName);
        notification[notice.level]({
          message: notice.title,
          description: notice.description,
          placement: 'bottomRight',
          duration: NOTICE_SECONDS[notice.level],
        });
      })
      .catch((error: unknown) => {
        // runIndexingJob reports its own failures as outcomes; this is a bug guard.
        notification.error({
          message: `No se pudo actualizar el Brain de ${organizationName}`,
          description: error instanceof Error ? error.message : 'Error inesperado',
          placement: 'bottomRight',
          duration: 0,
        });
      })
      .finally(() => {
        setRunning((count) => count - 1);
        setFinished((count) => count + 1);
      });
  }, []);

  useEffect(() => {
    if (running === 0) return undefined;
    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      // Browsers ignore the text and show their own, but the assignment is what triggers the prompt.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [running]);

  const value = useMemo(() => ({ trackIndexing, running, finished }), [trackIndexing, running, finished]);
  return <BrainIndexingContext.Provider value={value}>{children}</BrainIndexingContext.Provider>;
};

export const useBrainIndexing = (): BrainIndexingContextValue => useContext(BrainIndexingContext);
